import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Upload, FileCheck2 } from "lucide-react";

export function ClaimDialog({ facility, open, onOpenChange, user, onSubmitted }) {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    full_name: user?.name || "", title: "", relationship: "", phone: "", email: user?.email || "",
    proof: "", proof_document: "", declaration: false, notes: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleClose = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => setSubmitted(false), 200);
  };

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("proof_document", data.url); toast.success("Document uploaded"); }
    catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.full_name.trim()) errs.full_name = "Please enter your name.";
    if (!form.email.trim()) errs.email = "Please enter your work email.";
    if (!form.proof.trim()) errs.proof = "Please describe your proof of authority.";
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error("Please complete the required fields."); return; }
    if (!form.declaration) { toast.error("Please accept the legal declaration"); return; }
    setLoading(true);
    try {
      await api.post(`/facilities/${facility.facility_id}/claim`, form);
      setSubmitted(true);
      onSubmitted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit claim");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="claim-dialog">
        {submitted ? (
          <div data-testid="claim-success">
            <DialogHeader>
              <div className="mx-auto h-16 w-16 rounded-full bg-[#E3F3ED] flex items-center justify-center mb-2">
                <ShieldCheck className="h-8 w-8 text-[#0077B6]" />
              </div>
              <DialogTitle className="font-heading text-2xl text-center">Your claim is under review</DialogTitle>
              <DialogDescription className="text-center">
                Thanks! We've received your request to claim <b>{facility?.name}</b>. Our team will verify the details and email you at <b>{form.email}</b> once your listing is approved — usually within 1–2 business days.
              </DialogDescription>
            </DialogHeader>
            <div className="text-center">
              <Button onClick={() => handleClose(false)} data-testid="claim-success-close" className="mt-6 bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-11 px-8">
                Done
              </Button>
            </div>
          </div>
        ) : (
        <>
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-[#0077B6]" /> Claim {facility?.name}</DialogTitle>
          <DialogDescription>We verify every claim. Tell us who you are and your role at the facility, then confirm the declaration below.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label htmlFor="claim-name">Your name *</Label><Input id="claim-name" required data-testid="claim-name" aria-invalid={!!errors.full_name} aria-describedby={errors.full_name ? "claim-name-err" : undefined} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />{errors.full_name && <p id="claim-name-err" className="text-xs text-[#C0392B]">{errors.full_name}</p>}</div>
            <div className="space-y-1.5"><Label htmlFor="claim-title">Title / role</Label><Input id="claim-title" data-testid="claim-title" placeholder="Administrator" value={form.title} onChange={(e) => set("title", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label htmlFor="claim-email">Work email *</Label><Input id="claim-email" required type="email" data-testid="claim-email" aria-invalid={!!errors.email} aria-describedby={errors.email ? "claim-email-err" : undefined} value={form.email} onChange={(e) => set("email", e.target.value)} />{errors.email && <p id="claim-email-err" className="text-xs text-[#C0392B]">{errors.email}</p>}</div>
            <div className="space-y-1.5"><Label htmlFor="claim-phone">Phone</Label><Input id="claim-phone" data-testid="claim-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="claim-relationship">Your relationship to the facility</Label>
            <Input id="claim-relationship" data-testid="claim-relationship" placeholder="e.g. Owner, Executive Director, Marketing Director" value={form.relationship} onChange={(e) => set("relationship", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="claim-proof">Proof of authority *</Label>
            <Textarea id="claim-proof" required rows={3} data-testid="claim-proof" aria-invalid={!!errors.proof} aria-describedby={errors.proof ? "claim-proof-err" : undefined} placeholder="State license #, your role, work email domain matching the facility, or other verifiable proof that you're authorized to manage this listing." value={form.proof} onChange={(e) => set("proof", e.target.value)} />
            {errors.proof && <p id="claim-proof-err" className="text-xs text-[#C0392B]">{errors.proof}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="claim-doc-upload">Supporting document (optional)</Label>
            <label className="inline-flex items-center gap-2 px-4 h-10 rounded-xl border border-[#E8E6DF] cursor-pointer hover:border-[#1A365D] text-sm" data-testid="claim-doc-upload">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : form.proof_document ? <FileCheck2 className="h-4 w-4 text-[#0077B6]" /> : <Upload className="h-4 w-4" />}
              {form.proof_document ? "Document attached" : "Upload license / ID / badge"}
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} />
            </label>
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer bg-[#F7F6F2] rounded-xl p-3 border border-[#E8E6DF]" data-testid="claim-declaration-label">
            <Checkbox checked={form.declaration} onCheckedChange={(v) => set("declaration", !!v)} className="mt-0.5" data-testid="claim-declaration" />
            <span className="text-xs leading-relaxed text-[#4A534E]">
              I declare, under penalty of perjury, that I am authorized to represent and manage this facility's listing and that all information I have provided is true and accurate. I understand that submitting false information may result in removal and legal liability.
            </span>
          </label>
          <Button type="submit" disabled={loading || !form.declaration} data-testid="claim-submit" className="w-full bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-11">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit claim for verification"}
          </Button>
        </form>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
